import { Component, OnInit, HostListener } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { HttpClient } from '@angular/common/http';
import { Browser } from '@capacitor/browser';

@Component({
  selector: 'app-remote-html-viewer',
  templateUrl: './remote-html-viewer.component.html',
  styleUrls: ['./remote-html-viewer.component.scss']
})
export class RemoteHtmlViewerComponent implements OnInit {
  html!: SafeHtml;
  rawHtml = '';
  baseOrigin = '';

  constructor(
    private route: ActivatedRoute,
    private http: HttpClient,
    private sanitizer: DomSanitizer,
    private router: Router
  ) {}

  ngOnInit(): void {
    const url: string = this.route.snapshot.data['url'];
    const clean: boolean = this.route.snapshot.data['clean'] ?? true;
    if (!url) { this.html = this.sanitizer.bypassSecurityTrustHtml('<p>No URL configured</p>'); return; }

    try { this.baseOrigin = new URL(url).origin; } catch { this.baseOrigin = ''; }

    this.http.get(url, { responseType: 'text' }).subscribe(raw => {
      this.rawHtml = raw;
      const processed = clean ? this.processHtml(raw, url) : raw;
      this.html = this.sanitizer.bypassSecurityTrustHtml(processed);
    }, err => {
      this.html = this.sanitizer.bypassSecurityTrustHtml(`<p>Impossibile caricare la pagina: ${err.status || 'errore'}</p>`);
    });
  }

  private processHtml(raw: string, pageUrl: string): string {
    let out = raw;
    // remove scripts and inline event handlers
    out = out.replace(/<script[\s\S]*?<\/script>/gi, '');
    out = out.replace(/ on\w+="[^"]*"/gi, '');
    out = out.replace(/ on\w+='[^']*'/gi, '');
    // remove header/footer/nav
    out = out.replace(/<header[\s\S]*?<\/header>/gi, '');
    out = out.replace(/<footer[\s\S]*?<\/footer>/gi, '');
    out = out.replace(/<nav[\s\S]*?<\/nav>/gi, '');
    // optional: remove style blocks if too invasive
    // out = out.replace(/<style[\s\S]*?<\/style>/gi, '');

    // rewrite relative href/src to absolute using pageUrl origin
    const base = (() => {
      try { return new URL(pageUrl).origin + new URL(pageUrl).pathname.replace(/\/[^/]*$/,'/'); }
      catch { return this.baseOrigin || ''; }
    })();

    // href
    out = out.replace(/href=(['"])(?!https?:|mailto:|tel:|#)([^'"]+)\1/gi, (m, q, p) => {
      return `href=${q}${this.absUrl(base, p)}${q}`;
    });
    // src
    out = out.replace(/src=(['"])(?!https?:|data:|blob:)([^'"]+)\1/gi, (m, q, p) => {
      return `src=${q}${this.absUrl(base, p)}${q}`;
    });

    return out;
  }

  private absUrl(base: string, relative: string) {
    try {
      return new URL(relative, base).href;
    } catch {
      return relative;
    }
  }

  @HostListener('click', ['$event'])
  async onClick(ev: MouseEvent) {
    const target = (ev.target as HTMLElement).closest('a') as HTMLAnchorElement | null;
    if (!target) return;
    const href = target.getAttribute('href') ?? '';
    // ignore anchors
    if (href.startsWith('#')) return;

    // If it's a relative link to a known internal path (example: contains 'loyalty-terms' or '/loyalty-terms')
    if (href.includes('loyalty-terms') || href.startsWith('/loyalty-terms')) {
      ev.preventDefault();
      this.router.navigateByUrl('/loyalty-terms');
      return;
    }

    // If it's an app-internal route we want to handle via router (other rules can be added)
    if (href.startsWith('/') && !href.startsWith('//')) {
      ev.preventDefault();
      this.router.navigateByUrl(href);
      return;
    }

    // external: open with Capacitor Browser if available, else window.open
    ev.preventDefault();
    try {
      await Browser.open({ url: href.startsWith('http') ? href : this.absUrl(this.baseOrigin, href) });
    } catch {
      window.open(href.startsWith('http') ? href : this.absUrl(this.baseOrigin, href), '_blank');
    }
  }
}
